import { i18n } from 'shared/I18n'

import { useFeatures } from 'hooks/useFeatures'

import { type GlobalPageProps, type Role, type User } from 'helpers/interfaces'

import { Navbar, type NavbarProps } from './Navbar'
import type { NavbarLinkProps } from './types'

type PrimaryNavbarProps = {
  user: User
  classes?: NavbarProps['classes']
}

type NavbarTabs = {
  [key in Role]: NavbarLinkProps[]
}

const userGuideTab: NavbarLinkProps = {
  value: 'user-guide',
  label: 'User Guide',
  href: '/pages/getting_started',
}

const analyticsTab: NavbarLinkProps = {
  value: 'analytics',
  label: 'Analytics',
  href: '/analytics',
}

const sessionHistoryTab: NavbarLinkProps = {
  value: 'session_history',
  label: 'Session history',
  href: ['/dashboard', '/vr_sessions'],
}

const adminScenarios: NavbarLinkProps = {
  value: 'scenarios',
  label: 'Scenarios',
  href: '/admin/scenarios',
  type: 'react',
}

const adminLibraries: NavbarLinkProps = {
  value: 'libraries',
  label: 'Libraries',
  href: '/admin/libraries',
  type: 'react',
}

const adminCompetencies: NavbarLinkProps = {
  value: 'cfs',
  label: 'CFs',
  href: '/admin/competencies',
  type: 'react',
}

const adminUsers: NavbarLinkProps = {
  value: 'users',
  label: 'Users',
  href: '/admin/users',
  type: 'react',
}

const adminInstitutions: NavbarLinkProps = {
  value: 'institutions',
  label: 'Institutions',
  href: '/admin/institutions',
  type: 'react',
}

const adminInsights: NavbarLinkProps = {
  value: 'insights',
  label: i18n.t('insights.title'),
  href: '/admin/insights',
  featureFlag: 'insights-page',
  type: 'react',
}

const adminSubscriptions = (features: GlobalPageProps['features']): NavbarLinkProps => ({
  value: 'subscriptions',
  label: 'Subscriptions',
  href: '/admin/subscriptions',
  type: features['webapp-1208-admin-subscriptions-page'] ? 'react' : 'ruby',
})

export const getLinks = (features: GlobalPageProps['features']): NavbarTabs => ({
  institution_admin: [
    { value: 'scenarios', label: 'Scenarios', href: ['/institution_admin/vr_sessions', '/healthstream/scenarios'] },
    { value: 'users', label: 'Users', href: '/institution_admin/users' },
    { value: 'groups', label: 'Groups', href: '/institution_admin/groups' },
    analyticsTab,
    {
      value: 'insights',
      label: i18n.t('insights.title'),
      href: '/institution_admin/insights',
      featureFlag: 'insights-page',
      type: 'react',
    },
    { value: 'settings', label: 'Settings', href: '/institution_admin/settings', type: 'react' },
    userGuideTab,
  ],
  scenario_author: [
    { value: 'scenarios', label: 'Scenarios', href: '/scenario_author/scenarios' },
    sessionHistoryTab,
    analyticsTab,
    userGuideTab,
  ],
  student: [
    { value: 'scenarios', label: 'Scenarios', href: ['/scenarios', '/healthstream/scenarios'] },
    sessionHistoryTab,
    analyticsTab,
    userGuideTab,
  ],
  faculty_member: [
    {
      value: 'insights',
      label: i18n.t('insights.title'),
      href: '/faculty/insights',
      featureFlag: 'insights-page',
      type: 'react',
    },
    analyticsTab,
    userGuideTab,
  ],
  account_manager: [
    adminScenarios,
    adminLibraries,
    adminCompetencies,
    adminUsers,
    adminInstitutions,
    analyticsTab,
    adminInsights,
    adminSubscriptions(features),
  ],
  admin: [
    adminScenarios,
    adminLibraries,
    adminCompetencies,
    { value: 'admins', label: 'Admins', href: '/admin/admins' },
    adminUsers,
    adminInstitutions,
    analyticsTab,
    adminInsights,
    adminSubscriptions(features),
  ],
})

export const PrimaryNavbar = ({ user: { role }, classes }: PrimaryNavbarProps) => {
  const features = useFeatures()
  const links = getLinks(features)
  return <Navbar aria-label="main navigation" links={links[role]} classes={classes} />
}
